// Minimal in-app toast (transient notification) store.
//
// Why this exists: a web push that arrives while the app is open needs an
// in-app surface (lib/pushNotice.ts shows it here instead of as a system
// notification, issue 272), and so does any one-off result or failure. This
// is a tiny, dependency-free surface: a module-level store that any code calls
// via showToast(), rendered once by <Toaster /> in the AppShell. Intentionally
// small — no positioning options or action buttons, just a transient message
// with auto-dismiss and at most one link (a push notice leads somewhere, as
// its system notification would). Grow it if a real design need appears.

/** Where a toast leads: an app path ("/tickets/abc") or another site's URL. */
export interface ToastLink {
  href: string;
  label: string;
}

export interface Toast {
  id: string;
  title: string;
  body?: string;
  link?: ToastLink;
}

export interface ToastOptions {
  /** Auto-dismiss delay in ms; 0 keeps the toast until it is dismissed. */
  durationMs?: number;
  link?: ToastLink;
}

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let nextId = 0;

/** Cap the visible stack so a burst of messages can't fill the screen. */
const MAX_TOASTS = 4;
/** Auto-dismiss delay (ms) — long enough to read a short message. */
const DEFAULT_DURATION = 6000;

function emit(): void {
  for (const listener of listeners) listener(toasts);
}

/** Subscribe to toast changes; the listener is called immediately with the
 *  current list and on every change. Returns an unsubscribe function. */
export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  listener(toasts);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissToast(id: string): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Show a toast. Returns its id (so callers can dismiss it early). */
export function showToast(title: string, body?: string, options: ToastOptions = {}): string {
  const { durationMs = DEFAULT_DURATION, link } = options;
  // Monotonic id — no Date.now()/random, so it's deterministic and testable.
  const id = `toast-${(nextId += 1)}`;
  // slice(-MAX_TOASTS) drops the oldest when over the cap.
  toasts = [...toasts, { id, title, body, link }].slice(-MAX_TOASTS);
  emit();
  if (durationMs > 0) {
    setTimeout(() => dismissToast(id), durationMs);
  }
  return id;
}
